

import { useEffect, useMemo, useState } from "react";
import axios from "../api";

function getWeekAndYear(dateStr) {
  const d = new Date(dateStr);
  // ISO-8601 mit UTC, damit TZ keinen Einfluss hat
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = date.getUTCDay() || 7; // Montag=1..Sonntag=7
  // Auf Donnerstag normalisieren
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const isoYear = date.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const week = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return { week, year: isoYear };
}

export default function WeeklyPointsArchive() {
  const [items, setItems] = useState([]);
  const [date, setDate] = useState("");
  const [week, setWeek] = useState("");
  const [year, setYear] = useState("");
  const [usersMap, setUsersMap] = useState({}); // id -> displayName/username
  const [filterUserId, setFilterUserId] = useState("");

  useEffect(() => {
    let url = "/users/leaderboard/weekly/archive";
    if (week && year) url += `?week=${week}&year=${year}`;
    axios.get(url).then(res => setItems(res.data.items || []));
  }, [week, year]);

  // Load users list once to resolve display names
  useEffect(() => {
    let mounted = true;
    axios.get('/users/list').then(res => {
      if (!mounted) return;
      const arr = res.data?.items || [];
      const map = {};
      for (const u of arr) {
        if (!u) continue;
        const name = u.displayName || u.username || `User #${u.id}`;
        map[Number(u.id)] = name;
      }
      setUsersMap(map);
    }).catch(()=>{});
    return () => { mounted = false; };
  }, []);

  function handleDateChange(e) {
    const val = e.target.value;
    setDate(val);
    if (val) {
      const { week, year } = getWeekAndYear(val);
      setWeek(week);
      setYear(year);
    } else {
      setWeek("");
      setYear("");
    }
  }

  const grouped = useMemo(() => {
    const g = new Map(); // key: `${year}-W${week}` -> {year,week,entries:[], total, members}
    const base = items || [];
    const filtered = filterUserId ? base.filter(e => String(e.userId) === String(filterUserId)) : base;
    for (const e of filtered) {
      const key = `${e.year}-W${e.week}`;
      if (!g.has(key)) g.set(key, { year: e.year, week: e.week, entries: [], total: 0, members: new Set() });
      const bucket = g.get(key);
      bucket.entries.push(e);
      bucket.total += Number(e.points) || 0;
      if (e.userId != null) bucket.members.add(Number(e.userId));
    }
    const list = Array.from(g.values())
      .map(b => ({ ...b, memberCount: b.members.size }))
      .sort((a,b) => (b.year - a.year) || (b.week - a.week));
    // Sort entries inside each bucket by points desc
    list.forEach(b => b.entries.sort((a,b) => (Number(b.points)||0) - (Number(a.points)||0)));
    return list;
  }, [items, filterUserId]);

  return (
    <div className="weekly-archive">
      <div style={{marginBottom:16}}>
        <label style={{marginRight:8}}>
          Woche wählen:
          <input type="date" value={date} onChange={handleDateChange} style={{marginLeft:4}} />
        </label>
        <button onClick={()=>{setDate("");setWeek("");setYear("");}} style={{marginLeft:8}}>Alle anzeigen</button>
        {week && year && (
          <span style={{marginLeft:16}}>KW: <b>{week}</b> / Jahr: <b>{year}</b></span>
        )}
        <label style={{ marginLeft:24 }}>
          Member:
          <select value={filterUserId} onChange={e => setFilterUserId(e.target.value)} style={{marginLeft:6}}>
            <option value="">Alle</option>
            {Object.entries(usersMap)
              .sort((a,b)=> a[1].localeCompare(b[1], 'de', { sensitivity:'base' }))
              .map(([id,name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
          </select>
        </label>
        {filterUserId && grouped.length === 0 && (
          <span style={{ marginLeft:12, color:'#ff8484', fontSize:12 }}>Keine Einträge für ausgewählten Nutzer.</span>
        )}
      </div>
      {grouped.length === 0 && (
        <div style={{opacity:.8}}>Keine Daten für diese Auswahl.</div>
      )}
      {grouped.map(bucket => (
        <div key={`${bucket.year}-W${bucket.week}`} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:14, marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, flexWrap:'wrap', gap:8 }}>
            <div style={{ fontWeight:700 }}>KW {bucket.week} · {bucket.year}</div>
            <div style={{ fontSize:12, opacity:.85 }}>
              Teilnehmer: <b>{bucket.memberCount}</b> · Gesamtpunkte: <b>{bucket.total}</b>
            </div>
          </div>
          <ul style={{ listStyle:'none', margin:0, padding:0, display:'flex', flexDirection:'column', gap:6 }}>
            {bucket.entries.map(entry => (
              <li key={entry.id} style={{ display:'flex', justifyContent:'space-between', gap:12, padding:'8px 10px', background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:8 }}>
                <span>
                  <span style={{ fontWeight:600 }}>{usersMap[Number(entry.userId)] || `User #${entry.userId}`}</span>
                  <span style={{ opacity:.6, marginLeft:8 }}>(ID: {entry.userId})</span>
                </span>
                <span style={{ fontWeight:700, color:'#32CD89' }}>{entry.points}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
